import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  CContainer,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CSpinner,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormCheck,
  CRow,
  CCol,
  CBadge,
  CPagination,
  CPaginationItem,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilArrowLeft,
  cilChartPie,
  cilCalendar,
  cilList,
  cilTruck,
  cilMap,
  cilCloudDownload,
  cilWarning,
  cilPlus,
  cilPencil,
  cilTrash,
  cilLayers,
} from "@coreui/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import i18n from "../../../i18n";

registerLocale("ru", ru);

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return "00:00";
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return h === "00" ? `${m}:${s}` : `${h}:${m}:${s}`;
};

const getDeviceOffsetMin = (offsetMsk) =>
  (3 + (parseInt(offsetMsk, 10) || 0)) * 60;

const getFakeLocalDate = (isoString, offsetMsk) => {
  if (!isoString) return new Date();
  const offsetMin = getDeviceOffsetMin(offsetMsk);
  const d = dayjs(isoString).utcOffset(offsetMin);
  return new Date(
    d.year(),
    d.month(),
    d.date(),
    d.hour(),
    d.minute(),
    d.second(),
  );
};

const fromFakeLocalToUtcIso = (fakeDate, offsetMsk = 0) => {
  if (!fakeDate) return null;
  const offsetMin = getDeviceOffsetMin(offsetMsk);
  const utcMs = Date.UTC(
    fakeDate.getFullYear(),
    fakeDate.getMonth(),
    fakeDate.getDate(),
    fakeDate.getHours(),
    fakeDate.getMinutes(),
    fakeDate.getSeconds(),
  );
  return new Date(utcMs - offsetMin * 60000).toISOString();
};

const TruckFleetPage = () => {
  const { t } = useTranslation();
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isStaff = localStorage.getItem("is_staff") === "true" || isSuperuser;

  const [activeTab, setActiveTab] = useState("trucks");
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);

  const [trucks, setTrucks] = useState([]);
  const [devices, setDevices] = useState([]);
  const [bleTags, setBleTags] = useState([]);
  const [workZones, setWorkZones] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [defaultTzOffset, setDefaultTzOffset] = useState(0);

  // Фильтр по организациям (только для суперпользователя)
  const [selectedOrgFilter, setSelectedOrgFilter] = useState(
    isSuperuser ? localStorage.getItem("dashboardOrgFilter") || "" : ""
  );

  const [detailPage, setDetailPage] = useState(1);
  const itemsPerPage = 1000;

  // --- Модальное окно добавления/редактирования самосвала ---
  const [truckModal, setTruckModal] = useState({
    visible: false,
    isEdit: false,
    id: null,
    organization: "",
    vehicle_type: "",
    brand: "",
    plate_number: "",
    body_volume: 20.0,
    color: "#28a745",
    work_zone: "",
    ble_tag: "",
    installed_device: "",
    auto_cross_sync: true,
    omnicomm_id: "",
    strict_volume_limit: false,
    photo: null,
    photoPreview: null,
  });
  const [savingTruck, setSavingTruck] = useState(false);

  // --- Модальное окно удаления самосвала ---
  const [deleteTruckModal, setDeleteTruckModal] = useState({
    visible: false,
    truck: null,
  });
  const [deletingTruck, setDeletingTruck] = useState(false);

  // --- Модальное окно аналитики ---
  const [statsModal, setStatsModal] = useState(() => {
    const now = dayjs();
    const startFake = now.startOf("day").toDate();
    return {
      visible: false,
      targetTruck: null,
      loading: false,
      data: null,
      startDate: startFake,
      endDate: now.endOf("day").toDate(),
      activePreset: "today",
      filterMode: "custom",
      baseDate: startFake,
      selectedShiftId: "",
      schedule: null,
      shifts: [],
      isDataIncomplete: false,
      expectedVolume: 0,
      workZone: "",
    };
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [resTrucks, resTags, resZones, resTypes, resOrgs, resDevs] = await Promise.all([
        axios.get("/api/trucks/", { headers }),
        axios.get("/api/ble-tags/", { headers }),
        axios.get("/api/work-zones/", { headers }),
        axios.get("/api/vehicle-types/", { headers }),
        isSuperuser
          ? axios.get("/api/organizations/", { headers }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        axios.get("/api/devices/", { headers }).catch(() => ({ data: [] })),
      ]);

      const devsList = resDevs.data.results || resDevs.data || [];
      setDevices(devsList);
      setTrucks(resTrucks.data || []);
      setBleTags(resTags.data || []);
      setWorkZones(resZones.data || []);
      setVehicleTypes(resTypes.data.vehicle_types || resTypes.data || []);
      setOrganizations(resOrgs.data || []);

      if (devsList.length > 0) {
        setDefaultTzOffset(devsList[0].timezone_offset || 0);
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const uniqueOrganizations = useMemo(() => {
    if (!isSuperuser) return [];
    if (organizations.length > 0) {
      return organizations.map((o) => o.name).sort();
    }
    const orgs = new Set();
    trucks.forEach((t) => t.organization_name && orgs.add(t.organization_name));
    workZones.forEach((z) => z.organization_name && orgs.add(z.organization_name));
    bleTags.forEach((tag) => tag.organization_name && orgs.add(tag.organization_name));
    return Array.from(orgs).sort();
  }, [isSuperuser, organizations, trucks, workZones, bleTags]);

  const filteredTrucks = useMemo(() => {
    if (!isSuperuser || !selectedOrgFilter) return trucks;
    return trucks.filter((t) => t.organization_name === selectedOrgFilter);
  }, [isSuperuser, trucks, selectedOrgFilter]);

  const filteredWorkZones = useMemo(() => {
    if (!isSuperuser || !selectedOrgFilter) return workZones;
    return workZones.filter((z) => z.organization_name === selectedOrgFilter);
  }, [isSuperuser, workZones, selectedOrgFilter]);

  const filteredBleTags = useMemo(() => {
    if (!isSuperuser || !selectedOrgFilter) return bleTags;
    return bleTags.filter((tag) => tag.organization_name === selectedOrgFilter);
  }, [isSuperuser, bleTags, selectedOrgFilter]);

  // --- Функция интеллектуального определения нужного экскаватора для подтягивания смен ---
  const getRelevantDevice = useCallback((truck, zoneId = "", orgFilter = "") => {
    let candidate = null;

    // 1. По конкретному самосвалу
    if (truck) {
      if (truck.work_zone) {
        candidate = devices.find((d) => d.work_zone === truck.work_zone && d.has_multiple_shifts) ||
                    devices.find((d) => d.work_zone === truck.work_zone);
      }
      if (!candidate) {
        const orgMatch = truck.organization || truck.organization_name;
        if (orgMatch) {
          candidate = devices.find((d) => (d.organization === orgMatch || d.organization_name === orgMatch) && d.has_multiple_shifts) ||
                      devices.find((d) => (d.organization === orgMatch || d.organization_name === orgMatch));
        }
      }
    }

    // 2. По участку (если фильтр в модалке)
    if (!candidate && zoneId) {
      candidate = devices.find((d) => d.work_zone === parseInt(zoneId) && d.has_multiple_shifts) ||
                  devices.find((d) => d.work_zone === parseInt(zoneId));
    }

    // 3. По организации (из глобального фильтра)
    if (!candidate && orgFilter) {
      candidate = devices.find((d) => d.organization_name === orgFilter && d.has_multiple_shifts) ||
                  devices.find((d) => d.organization_name === orgFilter);
    }

    // 4. Фолбэк на любое устройство со сменами
    if (!candidate) {
      candidate = devices.find(d => d.has_multiple_shifts) || devices[0] || null;
    }
    
    return candidate;
  }, [devices]);

  // Обновление состояния смен и загрузка с бэкенда
  const updateModalDeviceContext = useCallback((truck, zone) => {
    const relDev = getRelevantDevice(truck, zone, selectedOrgFilter);
    const targetTz = relDev ? (relDev.timezone_offset || 0) : defaultTzOffset;
    
    const scheduleData = relDev ? {
      shift_start: relDev.shift_start || "00:00",
      shift_end: relDev.shift_end || "23:59",
      has_multiple_shifts: relDev.has_multiple_shifts || false,
      timezone_offset: targetTz
    } : { shift_start: "00:00", shift_end: "23:59", has_multiple_shifts: false, timezone_offset: 0 };

    setStatsModal(p => ({ ...p, schedule: scheduleData }));

    if (relDev && relDev.has_multiple_shifts) {
      const token = localStorage.getItem("token");
      axios.get(`/api/devices/${relDev.id}/shifts/`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setStatsModal(p => ({ ...p, shifts: Array.isArray(res.data) ? res.data : res.data.results || [] }));
        })
        .catch(err => console.warn("Не удалось загрузить смены"));
    } else {
      setStatsModal(p => ({ ...p, shifts: [] }));
    }

    return targetTz;
  }, [getRelevantDevice, selectedOrgFilter, defaultTzOffset]);

  const shiftOptions = useMemo(() => {
    const sched = statsModal.schedule;
    if (!sched) return [];
    if (sched.has_multiple_shifts) {
      return statsModal.shifts.map((s) => ({
        id: s.id.toString(),
        name: s.name,
        start: s.start_time,
        end: s.end_time,
      }));
    }
    return [
      {
        id: "default",
        name: "Основная смена",
        start: sched.shift_start || "00:00",
        end: sched.shift_end || "23:59",
      },
    ];
  }, [statsModal.schedule, statsModal.shifts]);

  const handleShiftFilterApply = (newShiftId, newBaseDate) => {
    setStatsModal((prev) => ({ ...prev, selectedShiftId: newShiftId, baseDate: newBaseDate }));
    if (!newShiftId || !newBaseDate) return;

    const shift = shiftOptions.find((s) => s.id === newShiftId);
    if (shift) {
      const startArr = (shift.start || "00:00").split(":");
      const endArr = (shift.end || "23:59").split(":");
      const newStart = new Date(newBaseDate);
      newStart.setHours(parseInt(startArr[0], 10), parseInt(startArr[1], 10), 0, 0);

      const newEnd = new Date(newBaseDate);
      newEnd.setHours(parseInt(endArr[0], 10), parseInt(endArr[1], 10), 59, 999);

      if (newEnd <= newStart) newEnd.setDate(newEnd.getDate() + 1);

      setStatsModal((prev) => ({ ...prev, startDate: newStart, endDate: newEnd, activePreset: "custom" }));
      
      const relDev = getRelevantDevice(statsModal.targetTruck, statsModal.workZone, selectedOrgFilter);
      const targetTz = relDev ? (relDev.timezone_offset || 0) : defaultTzOffset;
      loadTruckStats(statsModal.targetTruck, newStart, newEnd, statsModal.workZone, selectedOrgFilter, targetTz);
    }
  };

  // --- Управление самосвалами (Create / Edit / Delete) ---
  const openCreateTruckModal = () => {
    const matchedOrg = organizations.find((o) => o.name === selectedOrgFilter);
    setTruckModal({
      visible: true,
      isEdit: false,
      id: null,
      organization: matchedOrg ? matchedOrg.id : "",
      vehicle_type: "",
      brand: "",
      plate_number: "",
      body_volume: 20.0,
      color: "#28a745",
      work_zone: "",
      ble_tag: "",
      omnicomm_id: "",
      strict_volume_limit: false,
      photo: null,
      photoPreview: null,
      installed_device: "", 
      auto_cross_sync: true,
    });
  };

  const openEditTruckModal = (truck) => {
    setTruckModal({
      visible: true,
      isEdit: true,
      id: truck.id,
      organization: truck.organization || "",
      vehicle_type: truck.vehicle_type || "",
      brand: truck.brand || "",
      plate_number: truck.plate_number || "",
      body_volume: truck.body_volume || 20.0,
      color: truck.color || "#28a745",
      work_zone: truck.work_zone || "",
      ble_tag: truck.ble_tag || "",
      omnicomm_id: truck.omnicomm_id || "",
      strict_volume_limit: Boolean(truck.strict_volume_limit),
      photo: null,
      photoPreview: truck.photo || null,
      installed_device: truck.installed_device || "",
      auto_cross_sync: truck.auto_cross_sync !== undefined ? truck.auto_cross_sync : true,
    });
  };

  const handleSaveTruck = async () => {
    if (!truckModal.brand.trim() || !truckModal.plate_number.trim()) {
      toast.warn("toast.warn(i18n.t('truck_fleet_page.specify_brand_and_number', 'Укажите марку и гос. номер'))");
      return;
    }
    const vol = parseFloat(truckModal.body_volume);
    if (isNaN(vol) || vol <= 0) {
      toast.warn("toast.warn(i18n.t('truck_fleet_page.body_volume_must_be_positive', 'Объем кузова должен быть больше 0'))");
      return;
    }

    setSavingTruck(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("brand", truckModal.brand.trim());
      formData.append("plate_number", truckModal.plate_number.trim());
      formData.append("body_volume", vol);
      formData.append("color", truckModal.color || "#28a745");
      formData.append("strict_volume_limit", truckModal.strict_volume_limit);
      formData.append("work_zone", truckModal.work_zone || "");
      formData.append("ble_tag", truckModal.ble_tag || "");
      formData.append("installed_device", truckModal.installed_device || "");
      formData.append("auto_cross_sync", truckModal.auto_cross_sync);
      formData.append("omnicomm_id", truckModal.omnicomm_id || "");

      if (isSuperuser && truckModal.organization) {
        formData.append("organization", truckModal.organization);
      }

      if (truckModal.vehicle_type) {
        formData.append("vehicle_type", truckModal.vehicle_type);
      }

      if (truckModal.photo instanceof File) {
        formData.append("photo", truckModal.photo);
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      };

      if (truckModal.isEdit) {
        await axios.patch(`/api/trucks/${truckModal.id}/`, formData, { headers });
        toast.success("toast.success(i18n.t('truck_fleet_page.truck_updated', 'Самосвал успешно обновлен'))");
      } else {
        await axios.post("/api/trucks/", formData, { headers });
        toast.success("toast.success(i18n.t('truck_fleet_page.truck_added', 'Самосвал успешно добавлен'))");
      }

      setTruckModal((prev) => ({ ...prev, visible: false }));
      fetchData();
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail ||
        (typeof err.response?.data === "object"
          ? Object.entries(err.response.data)
            .map(([k, v]) => `${k}: ${v}`)
            .join(", ")
          : null) ||
        "toast.error(i18n.t('truck_fleet_page.truck_save_error', 'Ошибка сохранения самосвала'))";
      toast.error(errorMsg);
    } finally {
      setSavingTruck(false);
    }
  };

  const openDeleteTruckModal = (truck) => {
    setDeleteTruckModal({ visible: true, truck });
  };

  const handleDeleteTruck = async () => {
    if (!deleteTruckModal.truck) return;
    setDeletingTruck(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/trucks/${deleteTruckModal.truck.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("toast.success(i18n.t('truck_fleet_page.truck_deleted', 'Самосвал успешно удален'))");
      setDeleteTruckModal({ visible: false, truck: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "toast.error(i18n.t('truck_fleet_page.truck_delete_error', 'Ошибка при удалении самосвала'))");
    } finally {
      setDeletingTruck(false);
    }
  };

  // --- Загрузка аналитики самосвалов ---
  const loadTruckStats = async (truck, start, end, zoneId = "", orgName = "", passedTz = null) => {
    setDetailPage(1);
    setStatsModal((prev) => ({
      ...prev,
      loading: true,
      isDataIncomplete: false,
      expectedVolume: 0,
    }));

    try {
      const token = localStorage.getItem("token");
      const targetTz = passedTz !== null ? passedTz : (getRelevantDevice(truck, zoneId, orgName)?.timezone_offset || defaultTzOffset);

      const params = new URLSearchParams();
      if (start) params.append("start_date", fromFakeLocalToUtcIso(start, targetTz));
      if (end) params.append("end_date", fromFakeLocalToUtcIso(end, targetTz));
      if (truck) params.append("truck_id", truck.id);
      if (zoneId) params.append("work_zone_id", zoneId);
      if (orgName) params.append("organization_name", orgName);
      params.append("show_unknown", "true");

      const res = await axios.get(
        `/api/analytics/trucks/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      let isDataIncomplete = false;
      let expectedVolume = 0;

      if (start && end) {
        const diffHours = dayjs(end).diff(dayjs(start), "hour", true);

        if (diffHours > 0 && diffHours <= 48) {
          const histDays = 7;
          const historyStart = dayjs(start).subtract(histDays, "day").toDate();

          const histParams = new URLSearchParams();
          histParams.append("start_date", fromFakeLocalToUtcIso(historyStart, targetTz));
          histParams.append("end_date", fromFakeLocalToUtcIso(start, targetTz));
          if (truck) histParams.append("truck_id", truck.id);
          if (zoneId) histParams.append("work_zone_id", zoneId);
          if (orgName) histParams.append("organization_name", orgName);

          try {
            const histRes = await axios.get(
              `/api/analytics/trucks/?${histParams.toString()}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );

            const histVolume = histRes.data?.global_summary?.volume || 0;
            const volumePerHour = histVolume / (histDays * 24);
            const expectedCurrentVolume = volumePerHour * diffHours;
            const currentVolume = res.data?.global_summary?.volume || 0;

            if (expectedCurrentVolume > 20 && currentVolume < expectedCurrentVolume * 0.5) {
              isDataIncomplete = true;
              expectedVolume = expectedCurrentVolume;
            }
          } catch (e) {
            console.warn("Prediction check failed", e);
          }
        }
      }

      setStatsModal((prev) => ({
        ...prev,
        data: res.data,
        isDataIncomplete,
        expectedVolume,
      }));
    } catch (err) {
      toast.error("toast.error(i18n.t('truck_fleet_page.analytics_load_error', 'Не удалось загрузить аналитику'))");
    } finally {
      setStatsModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openStatsModal = (truck = null) => {
    const targetTz = updateModalDeviceContext(truck, statsModal.workZone);
    const offsetMin = getDeviceOffsetMin(targetTz);
    const d = dayjs().utcOffset(offsetMin);
    const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());

    const start = new Date(nowFake);
    start.setHours(0, 0, 0, 0);
    const end = new Date(nowFake);
    end.setHours(23, 59, 59, 999);

    setStatsModal((prev) => ({
      ...prev,
      visible: true,
      targetTruck: truck,
      startDate: start,
      endDate: end,
      activePreset: "today",
      filterMode: "custom",
      baseDate: start,
      selectedShiftId: "",
      data: null,
    }));

    loadTruckStats(truck, start, end, statsModal.workZone, selectedOrgFilter, targetTz);
  };

  const setQuickFilter = (preset) => {
    const relDev = getRelevantDevice(statsModal.targetTruck, statsModal.workZone, selectedOrgFilter);
    const targetTz = relDev ? (relDev.timezone_offset || 0) : defaultTzOffset;
    
    const offsetMin = getDeviceOffsetMin(targetTz);
    const d = dayjs().utcOffset(offsetMin);
    const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());

    let start = null, end = null;
    if (preset === "today") {
      start = new Date(nowFake);
      start.setHours(0, 0, 0, 0);
      end = new Date(nowFake);
      end.setHours(23, 59, 59, 999);
    } else if (preset === "yesterday") {
      start = new Date(nowFake);
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setHours(23, 59, 59, 999);
    } else if (preset === "week") {
      start = new Date(nowFake);
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end = new Date(nowFake);
      end.setHours(23, 59, 59, 999);
    } else if (preset === "month") {
      start = new Date(nowFake);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end = new Date(nowFake);
      end.setHours(23, 59, 59, 999);
    }

    setStatsModal((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
      activePreset: preset,
    }));
    loadTruckStats(statsModal.targetTruck, start, end, statsModal.workZone, selectedOrgFilter, targetTz);
  };

  const displayedDetails = useMemo(() => {
    return statsModal.data?.details || [];
  }, [statsModal.data?.details]);

  const handleExportExcel = async () => {
    if (!statsModal.data) return;
    setExportingExcel(true);

    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = "System AI";

      const wsSummary = wb.addWorksheet("Сводка по автопарку");
      wsSummary.addRow(["=== ИНФОРМАЦИЯ ОБ ОТЧЕТЕ ==="]).font = { bold: true };
      const periodValue = `${dayjs(statsModal.startDate).format("DD.MM.YYYY HH:mm")} - ${dayjs(statsModal.endDate).format("DD.MM.YYYY HH:mm")}`;
      wsSummary.addRow(["Период", periodValue]);

      const selectedZoneObj = filteredWorkZones.find((z) => z.id.toString() === statsModal.workZone);
      const zoneName = selectedZoneObj ? selectedZoneObj.name : "Все участки";
      wsSummary.addRow(["Участок", zoneName]);

      const targetDevName =
        statsModal.data.details.length > 0
          ? [...new Set(statsModal.data.details.map((d) => d.device_name))].join(", ")
          : "Все экскаваторы";
      wsSummary.addRow(["Техника", targetDevName]);
      wsSummary.addRow([]);

      wsSummary.addRow(["=== ОБЩАЯ СТАТИСТИКА ==="]).font = { bold: true };
      wsSummary.addRow(["Всего заездов", statsModal.data.global_summary.arrivals]);
      wsSummary.addRow(["Всего ковшей", statsModal.data.global_summary.cycles]);
      wsSummary.addRow(["Общий объем (м³)", statsModal.data.global_summary.volume]);
      wsSummary.addRow(["Ср. время загрузки", formatDuration(statsModal.data.global_summary.avg_time_sec)]);
      wsSummary.addRow(["Ср. ковшей на машину", statsModal.data.global_summary.avg_cycles]);
      wsSummary.addRow([]);

      wsSummary.addRow(["=== ГРУППИРОВКА ПО МАШИНАМ ==="]).font = { bold: true };
      const headerRow = wsSummary.addRow(["Самосвал", "Заездов", "Ср. Ковшей", "Ср. Время", "Суммарный Объем (м³)"]);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };

      statsModal.data.trucks_summary.forEach((t) => {
        const row = wsSummary.addRow([
          t.name,
          t.arrivals,
          t.avg_cycles,
          formatDuration(t.avg_time_sec),
          Number(t.volume),
        ]);
        if (t.id === "unidentified") {
          row.font = { italic: true, color: { argb: "FF6C757D" } };
        }
      });

      wsSummary.columns.forEach((col) => {
        col.width = 20;
      });
      wsSummary.getColumn(1).width = 35;

      const wsDetails = wb.addWorksheet("Детализация заездов");
      const detHeader = wsDetails.addRow([
        "Дата",
        "Время начала",
        "Время окончания",
        "Экскаватор",
        "Самосвал",
        "Объем ковша (м³)",
        "Время загрузки",
        "Ковшей",
        "Объем (м³)",
      ]);
      detHeader.font = { bold: true, color: { argb: "FFFFFFFF" } };
      detHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };

      const sortedDetails = [...displayedDetails].sort(
        (a, b) => new Date(a.start_time) - new Date(b.start_time),
      );

      sortedDetails.forEach((d) => {
        const fakeStart = getFakeLocalDate(d.start_time, d.timezone_offset);
        const fakeEnd = getFakeLocalDate(d.end_time, d.timezone_offset);
        const bucketVol = d.bucket_volume || (d.cycles > 0 ? Number((Number(d.volume) / d.cycles).toFixed(2)) : 0);

        const row = wsDetails.addRow([
          dayjs(fakeStart).format("DD.MM.YYYY"),
          dayjs(fakeStart).format("HH:mm:ss"),
          dayjs(fakeEnd).format("HH:mm:ss"),
          d.device_name,
          d.truck_name,
          bucketVol,
          formatDuration(d.duration_sec),
          d.cycles,
          Number(d.volume),
        ]);

        row.getCell(1).alignment = { horizontal: "center" };
        row.getCell(2).alignment = { horizontal: "center" };
        row.getCell(3).alignment = { horizontal: "center" };

        if (d.truck_id === "unidentified") {
          row.font = { italic: true, color: { argb: "FF6C757D" } };
        }
      });

      wsDetails.columns.forEach((col) => {
        col.width = 18;
      });
      wsDetails.getColumn(4).width = 25;
      wsDetails.getColumn(5).width = 30;

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `i18n.t('truck_fleet_page.excel_report_name', 'Автопарк_Аналитика_') + dayjs().format('DD_MM_YYYY')${dayjs().format("DD_MM_YYYY")}.xlsx`);

      toast.success("Отчет Excel успешно скачан!");
    } catch (e) {
      toast.error("Ошибка при экспорте в Excel");
      console.error(e);
    } finally {
      setExportingExcel(false);
    }
  };

  const availableBleTags = filteredBleTags.filter(
    (tag) => !tag.assigned_truck || (truckModal.isEdit && tag.id === truckModal.ble_tag)
  );

  return (
    <div className="bg-light min-vh-100 p-3 p-md-4">
      <CContainer fluid>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 page-header-mobile">
          <div>
            <h2>{i18n.t('truck_fleet_page.fleet_management_title', 'Управление парком и объектами')}</h2>
            <div className="text-muted small">
              Управление самосвалами, участками работ и BLE-метками.
            </div>
          </div>
          <CButton
            color="secondary"
            as={Link}
            to="/dashboard"
            className="btn-mobile-100 mt-2 mt-md-0"
          >
            <CIcon icon={cilArrowLeft} className="me-2" /> Назад на дашборд
          </CButton>
        </div>

        {/* --- ФИЛЬТР ПО ОРГАНИЗАЦИИ (ТОЛЬКО ДЛЯ СУПЕРПОЛЬЗОВАТЕЛЯ) --- */}
        {isSuperuser && (
          <div className="bg-white p-3 rounded shadow-sm border mb-4 d-flex align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-2">
              <CIcon icon={cilLayers} className="text-primary" size="lg" />
              <span className="fw-bold text-dark small text-uppercase">
                Организация:
              </span>
            </div>
            <CFormSelect
              size="sm"
              style={{ minWidth: "260px", maxWidth: "380px" }}
              className="fw-bold text-primary border-primary shadow-sm"
              value={selectedOrgFilter}
              onChange={(e) => setSelectedOrgFilter(e.target.value)}
            >
              <option value="">-- Все организации --</option>
              {uniqueOrganizations.map((org) => (
                <option key={org} value={org}>
                  {org}
                </option>
              ))}
            </CFormSelect>
            {selectedOrgFilter && (
              <CButton
                color="secondary"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedOrgFilter("")}
              >
                {i18n.t('truck_fleet_page.reset_filter', 'Сбросить фильтр')}
              </CButton>
            )}
          </div>
        )}

        <CCard className="shadow-sm border-0">
          <CCardHeader className="bg-white border-bottom">
            <CNav variant="tabs" card className="mobile-scroll-tabs">
              <CNavItem>
                <CNavLink
                  active={activeTab === "trucks"}
                  onClick={() => setActiveTab("trucks")}
                  style={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    color: activeTab === "trucks" ? "#321fdb" : "",
                  }}
                >
                  <CIcon icon={cilTruck} className="me-2" />
                  Самосвалы ({filteredTrucks.length})
                </CNavLink>
              </CNavItem>
              <CNavItem>
                <CNavLink
                  active={activeTab === "zones"}
                  onClick={() => setActiveTab("zones")}
                  style={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    color: activeTab === "zones" ? "#321fdb" : "",
                  }}
                >
                  <CIcon icon={cilMap} className="me-2" />
                  Участки ({filteredWorkZones.length})
                </CNavLink>
              </CNavItem>
              <CNavItem>
                <CNavLink
                  active={activeTab === "tags"}
                  onClick={() => setActiveTab("tags")}
                  style={{
                    cursor: "pointer",
                    fontWeight: "bold",
                    color: activeTab === "tags" ? "#321fdb" : "",
                  }}
                >
                  <CIcon icon={cilLayers} className="me-2" />
                  BLE Метки ({filteredBleTags.length})
                </CNavLink>
              </CNavItem>
            </CNav>
          </CCardHeader>

          <CCardBody>
            <CTabContent>
              {/* --- ВКЛАДКА МАШИНЫ --- */}
              <CTabPane visible={activeTab === "trucks"}>
                <div className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div className="d-flex gap-2">
                    {isStaff && (
                      <CButton
                        color="primary"
                        onClick={openCreateTruckModal}
                        className="btn-mobile-100 fw-bold shadow-sm"
                      >
                        <CIcon icon={cilPlus} className="me-2" /> Добавить самосвал
                      </CButton>
                    )}
                  </div>
                  <CButton
                    color="info"
                    variant="outline"
                    onClick={() => openStatsModal(null)}
                    className="btn-mobile-100 fw-bold"
                  >
                    <CIcon icon={cilChartPie} className="me-2" /> {i18n.t('truck_fleet_page.summary_analytics', 'Сводная аналитика по автопарку')}
                  </CButton>
                </div>
                {loading ? (
                  <div className="text-center p-5">
                    <CSpinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CTable hover responsive align="middle" className="mb-0">
                      <CTableHead color="light">
                        <CTableRow>
                          <CTableHeaderCell>Марка</CTableHeaderCell>
                          <CTableHeaderCell className="text-center">Фото</CTableHeaderCell>
                          <CTableHeaderCell>Гос. Номер</CTableHeaderCell>
                          {isSuperuser && !selectedOrgFilter && (
                            <CTableHeaderCell>Организация</CTableHeaderCell>
                          )}
                          <CTableHeaderCell>Тип техники</CTableHeaderCell>
                          <CTableHeaderCell>Участок (Объект)</CTableHeaderCell>
                          <CTableHeaderCell>Объем (м³)</CTableHeaderCell>
                          <CTableHeaderCell>Лимит ИИ</CTableHeaderCell>
                          <CTableHeaderCell>Привязка BLE</CTableHeaderCell>
                          <CTableHeaderCell>Лицензия</CTableHeaderCell>
                          <CTableHeaderCell className="text-center">
                            История рейсов
                          </CTableHeaderCell>
                          {isStaff && (
                            <CTableHeaderCell className="text-end pe-3">
                              Действия
                            </CTableHeaderCell>
                          )}
                        </CTableRow>
                      </CTableHead>
                      <CTableBody>
                        {filteredTrucks.map((tr) => (
                          <CTableRow key={tr.id}>
                            <CTableDataCell>
                              <div className="d-flex align-items-center fw-bold">
                                <div
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: tr.color || "#28a745",
                                    borderRadius: "50%",
                                    marginRight: "10px",
                                    flexShrink: 0,
                                  }}
                                ></div>
                                {tr.brand}
                              </div>
                            </CTableDataCell>

                            {/* ФОТО */}
                            <CTableDataCell className="text-center">
                              {tr.photo ? (
                                <img
                                  src={tr.photo}
                                  alt="truck"
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    border: "1px solid #dee2e6",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    backgroundColor: "#e9ecef",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "0 auto",
                                  }}
                                >
                                  <CIcon icon={cilTruck} className="text-muted" />
                                </div>
                              )}
                            </CTableDataCell>

                            <CTableDataCell>
                              <strong>{tr.plate_number}</strong>
                            </CTableDataCell>

                            {isSuperuser && !selectedOrgFilter && (
                              <CTableDataCell>
                                <span className="small text-muted fw-semibold">
                                  {tr.organization_name || "—"}
                                </span>
                              </CTableDataCell>
                            )}

                            <CTableDataCell>
                              <span className="badge bg-light text-dark border fw-bold d-inline-flex align-items-center gap-1">
                                {tr.vehicle_type_icon?.startsWith("http") ? (
                                  <img src={tr.vehicle_type_icon} alt="" style={{ width: "16px", height: "16px", objectFit: "contain" }} />
                                ) : (
                                  <span>{tr.vehicle_type_icon || "🚚"}</span>
                                )}
                                <span>{tr.vehicle_type_name || "Самосвал"}</span>
                              </span>
                            </CTableDataCell>

                            <CTableDataCell>
                              {tr.work_zone_name ? (
                                <CBadge color="primary" shape="rounded-pill">
                                  {tr.work_zone_name}
                                </CBadge>
                              ) : (
                                <span className="text-muted small">Не привязан</span>
                              )}
                            </CTableDataCell>

                            <CTableDataCell className="fw-semibold">
                              {tr.body_volume} м³
                            </CTableDataCell>

                            <CTableDataCell>
                              {tr.strict_volume_limit ? (
                                <span className="text-danger fw-bold">Строгий</span>
                              ) : (
                                <span className="text-muted">Откл.</span>
                              )}
                            </CTableDataCell>

                            <CTableDataCell>
                              <div className="d-flex flex-column gap-1">
                                {tr.ble_tag_details ? (
                                  <span className="text-primary fw-semibold font-monospace small">
                                    📶 {tr.ble_tag_details.internal_id || tr.ble_tag_details.mac_address}
                                  </span>
                                ) : (
                                  <span className="text-muted small">Нет BLE</span>
                                )}
                                {tr.installed_device_name ? (
                                  <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 small fw-semibold">
                                    📱 {tr.installed_device_name}
                                  </span>
                                ) : (
                                  <span className="text-muted small" style={{ fontSize: "11px" }}>Нет планшета</span>
                                )}
                              </div>
                            </CTableDataCell>

                            <CTableDataCell>
                              {(() => {
                                if (!tr.ble_tag_details)
                                  return <span className="text-muted small">-</span>;
                                if (!tr.ble_tag_details.license_end_date)
                                  return <span className="text-warning small fw-bold">Не задана</span>;

                                const isExpired = dayjs(tr.ble_tag_details.license_end_date).isBefore(
                                  dayjs().startOf("day"),
                                );
                                const dateStr = dayjs(tr.ble_tag_details.license_end_date).format("DD.MM.YYYY");

                                return (
                                  <span className={`fw-bold small ${isExpired ? "text-danger" : "text-success"}`}>
                                    до {dateStr}
                                  </span>
                                );
                              })()}
                            </CTableDataCell>

                            {/* ИСТОРИЯ РЕЙСОВ */}
                            <CTableDataCell className="text-center">
                              <CButton
                                size="sm"
                                color="info"
                                variant="outline"
                                className="fw-bold text-nowrap"
                                onClick={() => openStatsModal(tr)}
                              >
                                <CIcon icon={cilList} className="me-1" /> Показать
                              </CButton>
                            </CTableDataCell>

                            {/* ДЕЙСТВИЯ (Редактировать / Удалить) */}
                            {isStaff && (
                              <CTableDataCell className="text-end pe-3 text-nowrap">
                                <CButton
                                  size="sm"
                                  color="secondary"
                                  variant="ghost"
                                  className="me-1"
                                  title="Редактировать"
                                  onClick={() => openEditTruckModal(tr)}
                                >
                                  <CIcon icon={cilPencil} />
                                </CButton>
                                <CButton
                                  size="sm"
                                  color="danger"
                                  variant="ghost"
                                  title="Удалить"
                                  onClick={() => openDeleteTruckModal(tr)}
                                >
                                  <CIcon icon={cilTrash} />
                                </CButton>
                              </CTableDataCell>
                            )}
                          </CTableRow>
                        ))}
                        {filteredTrucks.length === 0 && (
                          <CTableRow>
                            <CTableDataCell
                              colSpan={isStaff ? 12 : 11}
                              className="text-center text-muted p-4"
                            >
                              {i18n.t('truck_fleet_page.no_trucks', 'Самосвалов пока нет')}
                            </CTableDataCell>
                          </CTableRow>
                        )}
                      </CTableBody>
                    </CTable>
                  </div>
                )}
              </CTabPane>

              {/* --- ВКЛАДКА УЧАСТКИ --- */}
              <CTabPane visible={activeTab === "zones"}>
                {loading ? (
                  <div className="text-center p-5">
                    <CSpinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CTable hover responsive align="middle">
                      <CTableHead color="light">
                        <CTableRow>
                          <CTableHeaderCell>Название участка</CTableHeaderCell>
                          {isSuperuser && !selectedOrgFilter && (
                            <CTableHeaderCell>Организация</CTableHeaderCell>
                          )}
                          <CTableHeaderCell>Описание</CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody>
                        {filteredWorkZones.map((zone) => (
                          <CTableRow key={zone.id}>
                            <CTableDataCell className="fw-bold text-primary">{zone.name}</CTableDataCell>
                            {isSuperuser && !selectedOrgFilter && (
                              <CTableDataCell>
                                <span className="small text-muted fw-semibold">
                                  {zone.organization_name || "—"}
                                </span>
                              </CTableDataCell>
                            )}
                            <CTableDataCell className="text-muted">{zone.description || "-"}</CTableDataCell>
                          </CTableRow>
                        ))}
                        {filteredWorkZones.length === 0 && (
                          <CTableRow>
                            <CTableDataCell colSpan={3} className="text-center text-muted p-4">
                              {i18n.t('truck_fleet_page.no_work_zones', 'Участки пока не созданы.')}
                            </CTableDataCell>
                          </CTableRow>
                        )}
                      </CTableBody>
                    </CTable>
                  </div>
                )}
              </CTabPane>

              {/* --- ВКЛАДКА МЕТКИ --- */}
              <CTabPane visible={activeTab === "tags"}>
                {loading ? (
                  <div className="text-center p-5">
                    <CSpinner color="primary" />
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CTable hover responsive align="middle">
                      <CTableHead color="light">
                        <CTableRow>
                          <CTableHeaderCell>MAC Адрес</CTableHeaderCell>
                          <CTableHeaderCell>Внутренний ID</CTableHeaderCell>
                          {isSuperuser && !selectedOrgFilter && (
                            <CTableHeaderCell>Организация</CTableHeaderCell>
                          )}
                          <CTableHeaderCell>Статус привязки</CTableHeaderCell>
                          <CTableHeaderCell>Срок лицензии</CTableHeaderCell>
                          <CTableHeaderCell className="text-center">Договор</CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody>
                        {filteredBleTags.map((tag) => {
                          const isExpired = tag.license_end_date && dayjs(tag.license_end_date).isBefore(dayjs());

                          return (
                            <CTableRow key={tag.id} color={isExpired ? "danger" : ""}>
                              <CTableDataCell className="font-monospace fw-bold">{tag.mac_address}</CTableDataCell>
                              <CTableDataCell className="fw-bold">{tag.internal_id || "-"}</CTableDataCell>
                              {isSuperuser && !selectedOrgFilter && (
                                <CTableDataCell>
                                  <span className="small text-muted fw-semibold">
                                    {tag.organization_name || "—"}
                                  </span>
                                </CTableDataCell>
                              )}
                              <CTableDataCell>
                                {tag.assigned_truck ? (
                                  <span className="text-success fw-bold">На авто: {tag.assigned_truck}</span>
                                ) : (
                                  <span className="text-muted fw-bold">Свободна</span>
                                )}
                              </CTableDataCell>

                              <CTableDataCell>
                                {tag.license_end_date ? (
                                  <span className={`fw-bold small ${isExpired ? "text-danger" : "text-dark"}`}>
                                    до {dayjs(tag.license_end_date).format("DD.MM.YYYY")}
                                  </span>
                                ) : (
                                  <span className="text-muted small">Не задано</span>
                                )}
                              </CTableDataCell>

                              <CTableDataCell className="text-center">
                                {tag.license_file ? (
                                  <a href={tag.license_file} target="_blank" rel="noopener noreferrer">
                                    <CButton size="sm" color="info" variant="ghost" title="Скачать/Посмотреть договор">
                                      <CIcon icon={cilCloudDownload} />
                                    </CButton>
                                  </a>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </CTableDataCell>
                            </CTableRow>
                          );
                        })}
                        {filteredBleTags.length === 0 && (
                          <CTableRow>
                            <CTableDataCell colSpan={6} className="text-center text-muted p-4">
                              {i18n.t('truck_fleet_page.no_ble_tags', 'Меток пока нет')}
                            </CTableDataCell>
                          </CTableRow>
                        )}
                      </CTableBody>
                    </CTable>
                  </div>
                )}
              </CTabPane>
            </CTabContent>
          </CCardBody>
        </CCard>
      </CContainer>

      {/* --- МОДАЛЬНОЕ ОКНО ДОБАВЛЕНИЯ / РЕДАКТИРОВАНИЯ САМОСВАЛА --- */}
      <CModal
        visible={truckModal.visible}
        onClose={() => setTruckModal((prev) => ({ ...prev, visible: false }))}
        alignment="center"
        size="lg"
      >
        <CModalHeader className="bg-light">
          <CModalTitle className="fw-bold">
            <CIcon icon={cilTruck} className="me-2 text-primary" />
            {truckModal.isEdit ? "Редактировать самосвал" : "Добавить новый самосвал"}
          </CModalTitle>
        </CModalHeader>

        <CModalBody className="p-4">
          <CForm>
            <CRow className="g-3">
              {isSuperuser && (
                <CCol md={6}>
                  <CFormLabel className="fw-semibold">Организация</CFormLabel>
                  <CFormSelect
                    value={truckModal.organization || ""}
                    onChange={(e) => setTruckModal({ ...truckModal, organization: e.target.value })}
                  >
                    <option value="">-- Выберите организацию --</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
              )}

              <CCol md={6}>
                <CFormLabel className="fw-semibold">Тип техники</CFormLabel>
                <CFormSelect
                  value={truckModal.vehicle_type || ""}
                  onChange={(e) => setTruckModal({ ...truckModal, vehicle_type: e.target.value })}
                >
                  <option value="">-- Не указан --</option>
                  {vehicleTypes.map((vt) => (
                    <option key={vt.id} value={vt.id}>
                      {vt.icon ? `${vt.icon} ` : ""}{vt.name}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">
                  Марка автомобиля <span className="text-danger">*</span>
                </CFormLabel>
                <CFormInput
                  placeholder="Например: КАМАЗ 6520, БелАЗ..."
                  value={truckModal.brand}
                  onChange={(e) => setTruckModal({ ...truckModal, brand: e.target.value })}
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">
                  Государственный номер <span className="text-danger">*</span>
                </CFormLabel>
                <CFormInput
                  placeholder="Например: A 123 BC 77"
                  value={truckModal.plate_number}
                  onChange={(e) => setTruckModal({ ...truckModal, plate_number: e.target.value })}
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">
                  Вместительность кузова (м³) <span className="text-danger">*</span>
                </CFormLabel>
                <CFormInput
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={truckModal.body_volume}
                  onChange={(e) => setTruckModal({ ...truckModal, body_volume: e.target.value })}
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">Цвет на графиках</CFormLabel>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={truckModal.color}
                    onChange={(e) => setTruckModal({ ...truckModal, color: e.target.value })}
                    style={{ width: "45px", height: "38px" }}
                  />
                  <CFormInput
                    type="text"
                    value={truckModal.color}
                    onChange={(e) => setTruckModal({ ...truckModal, color: e.target.value })}
                    placeholder="#28a745"
                  />
                </div>
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">Участок (Объект)</CFormLabel>
                <CFormSelect
                  value={truckModal.work_zone}
                  onChange={(e) => setTruckModal({ ...truckModal, work_zone: e.target.value })}
                >
                  <option value="">-- Без привязки --</option>
                  {filteredWorkZones.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">Привязанная BLE-метка</CFormLabel>
                <CFormSelect
                  value={truckModal.ble_tag}
                  onChange={(e) => setTruckModal({ ...truckModal, ble_tag: e.target.value })}
                >
                  <option value="">-- Без метки --</option>
                  {availableBleTags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.internal_id || tag.mac_address}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">
                  📱 Бортовой планшет (IMU)
                </CFormLabel>
                <CFormSelect
                  value={truckModal.installed_device}
                  onChange={(e) => setTruckModal({ ...truckModal, installed_device: e.target.value })}
                >
                  <option value="">-- Без планшета --</option>
                  {devices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.mac_address})
                    </option>
                  ))}
                </CFormSelect>
              </CCol>

              <CCol md={6} className="d-flex align-items-center mt-4">
                <CFormCheck
                  id="auto_cross_sync_check"
                  label={<span className="fw-semibold">Авто-синхронизация по ударам ковша (IMU)</span>}
                  checked={truckModal.auto_cross_sync}
                  onChange={(e) => setTruckModal({ ...truckModal, auto_cross_sync: e.target.checked })}
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">ID в Omnicomm (для авто-поиска GPS)</CFormLabel>
                <CFormInput
                  type="number"
                  placeholder="Например: 1198021083"
                  value={truckModal.omnicomm_id}
                  onChange={(e) => setTruckModal({ ...truckModal, omnicomm_id: e.target.value })}
                />
              </CCol>

              <CCol md={6}>
                <CFormLabel className="fw-semibold">Фото грузовика</CFormLabel>
                <CFormInput
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setTruckModal({
                        ...truckModal,
                        photo: file,
                        photoPreview: URL.createObjectURL(file),
                      });
                    }
                  }}
                />
                {truckModal.photoPreview && (
                  <div className="mt-2">
                    <img
                      src={truckModal.photoPreview}
                      alt="Превью"
                      style={{ maxHeight: "70px", borderRadius: "6px", objectFit: "cover" }}
                    />
                  </div>
                )}
              </CCol>

              <CCol xs={12}>
                <CFormCheck
                  id="strict_volume_limit"
                  label={
                    <span className="fw-semibold">
                      Строгий лимит по объему (отсекать заезды при превышении объема кузова)
                    </span>
                  }
                  checked={truckModal.strict_volume_limit}
                  onChange={(e) =>
                    setTruckModal({ ...truckModal, strict_volume_limit: e.target.checked })
                  }
                />
              </CCol>
            </CRow>
          </CForm>
        </CModalBody>

        <CModalFooter className="bg-light">
          <CButton
            color="secondary"
            variant="ghost"
            onClick={() => setTruckModal((prev) => ({ ...prev, visible: false }))}
            disabled={savingTruck}
          >
            Отмена
          </CButton>
          <CButton
            color="primary"
            onClick={handleSaveTruck}
            disabled={savingTruck}
            className="fw-bold px-4 text-white"
          >
            {savingTruck ? <CSpinner size="sm" className="me-2" /> : null}
            {truckModal.isEdit ? "Сохранить изменения" : "Добавить"}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* --- МОДАЛЬНОЕ ОКНО ПОДТВЕРЖДЕНИЯ УДАЛЕНИЯ САМОСВАЛА --- */}
      <CModal
        visible={deleteTruckModal.visible}
        onClose={() => setDeleteTruckModal({ visible: false, truck: null })}
        alignment="center"
      >
        <CModalHeader className="bg-danger text-white">
          <CModalTitle className="fw-bold">Удаление самосвала</CModalTitle>
        </CModalHeader>
        <CModalBody className="p-4">
          <p className="mb-1">
            Вы уверены, что хотите удалить самосвал{" "}
            <strong>
              {deleteTruckModal.truck?.brand} ({deleteTruckModal.truck?.plate_number})
            </strong>
            ?
          </p>
          <p className="text-muted small mb-0">
            История ранее сохраненных рейсов сохранится, но машина будет отвязана.
          </p>
        </CModalBody>
        <CModalFooter className="bg-light">
          <CButton
            color="secondary"
            variant="ghost"
            onClick={() => setDeleteTruckModal({ visible: false, truck: null })}
            disabled={deletingTruck}
          >
            Отмена
          </CButton>
          <CButton
            color="danger"
            className="text-white fw-bold px-4"
            onClick={handleDeleteTruck}
            disabled={deletingTruck}
          >
            {deletingTruck ? <CSpinner size="sm" className="me-2" /> : null}
            Удалить
          </CButton>
        </CModalFooter>
      </CModal>

      {/* --- МОДАЛЬНОЕ ОКНО АНАЛИТИКИ САМОСВАЛОВ (ТОЛЬКО ЧТЕНИЕ) --- */}
      <CModal
        alignment="center"
        size="xl"
        visible={statsModal.visible}
        onClose={() => setStatsModal({ ...statsModal, visible: false })}
      >
        <CModalHeader className="bg-light border-bottom-0 pb-3">
          <CModalTitle className="d-flex align-items-center fw-bold mb-0">
            <CIcon icon={cilTruck} className="me-2 text-primary" size="xl" />
            {statsModal.targetTruck
              ? `Аналитика: ${statsModal.targetTruck.brand} (${statsModal.targetTruck.plate_number})`
              : "Сводная аналитика автопарка"}
          </CModalTitle>
        </CModalHeader>

        <CModalBody className="p-4 bg-light">
          <div className="bg-white p-3 rounded shadow-sm border mb-4">
            
            {/* ПЕРЕКЛЮЧАТЕЛЬ РЕЖИМА */}
            <div className="mb-3 d-flex gap-4 border-bottom pb-3">
              <CFormCheck
                type="radio"
                name="truckFilterMode"
                id="truckModeCustom"
                label={<span className="fw-bold text-dark">Произвольный период</span>}
                checked={statsModal.filterMode === "custom"}
                onChange={() => setStatsModal(p => ({ ...p, filterMode: "custom" }))}
              />
              <CFormCheck
                type="radio"
                name="truckFilterMode"
                id="truckModeShift"
                label={<span className="fw-bold text-dark">По сменам</span>}
                checked={statsModal.filterMode === "shift"}
                onChange={() => setStatsModal(p => ({ ...p, filterMode: "shift" }))}
              />
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              
              <div className="d-flex flex-column gap-3 w-100">
                {statsModal.filterMode === "custom" ? (
                  <>
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <span className="fw-bold text-muted me-2 d-flex align-items-center">
                        <CIcon icon={cilCalendar} className="me-1" /> Период:
                      </span>
                      <div className="d-flex flex-wrap gap-2">
                        {[
                          { id: "today", label: "Сегодня" },
                          { id: "yesterday", label: "Вчера" },
                          { id: "week", label: "7 дней" },
                          { id: "month", label: "Месяц" },
                        ].map((p) => {
                          const isActive = statsModal.activePreset === p.id;
                          return (
                            <CButton
                              key={p.id}
                              color={isActive ? "primary" : "light"}
                              size="sm"
                              className={`rounded-pill px-3 fw-semibold ${isActive ? "text-white shadow-sm" : "border text-dark"}`}
                              onClick={() => setQuickFilter(p.id)}
                            >
                              {p.label}
                            </CButton>
                          );
                        })}
                      </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-3 bg-light p-2 rounded border">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small fw-semibold me-2">С:</span>
                        <DatePicker
                          selected={statsModal.startDate}
                          onChange={(date) => {
                            setStatsModal((prev) => ({ ...prev, startDate: date, activePreset: "custom" }));
                            if (date && statsModal.endDate) {
                              const relDev = getRelevantDevice(statsModal.targetTruck, statsModal.workZone, selectedOrgFilter);
                              const targetTz = relDev ? (relDev.timezone_offset || 0) : defaultTzOffset;
                              loadTruckStats(statsModal.targetTruck, date, statsModal.endDate, statsModal.workZone, selectedOrgFilter, targetTz);
                            }
                          }}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          className="form-control form-control-sm text-center fw-semibold"
                          dateFormat="dd.MM.yyyy HH:mm"
                          locale="ru"
                          placeholderText="Начало"
                          popperProps={{ strategy: "fixed" }}
                          portalId="root-portal"
                        />
                      </div>

                      <div className="d-flex align-items-center">
                        <span className="text-muted small fw-semibold me-2">По:</span>
                        <DatePicker
                          selected={statsModal.endDate}
                          onChange={(date) => {
                            setStatsModal((prev) => ({ ...prev, endDate: date, activePreset: "custom" }));
                            if (statsModal.startDate && date) {
                              const relDev = getRelevantDevice(statsModal.targetTruck, statsModal.workZone, selectedOrgFilter);
                              const targetTz = relDev ? (relDev.timezone_offset || 0) : defaultTzOffset;
                              loadTruckStats(statsModal.targetTruck, statsModal.startDate, date, statsModal.workZone, selectedOrgFilter, targetTz);
                            }
                          }}
                          showTimeSelect
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          className="form-control form-control-sm text-center fw-semibold"
                          dateFormat="dd.MM.yyyy HH:mm"
                          locale="ru"
                          placeholderText="Конец"
                          popperProps={{ strategy: "fixed" }}
                          portalId="root-portal"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="d-flex flex-wrap align-items-center gap-3 bg-light p-2 rounded border">
                    <div className="d-flex align-items-center">
                      <span className="text-muted small fw-semibold me-2">Дата смены:</span>
                      <DatePicker
                        selected={statsModal.baseDate}
                        onChange={(date) => handleShiftFilterApply(statsModal.selectedShiftId, date)}
                        className="form-control form-control-sm text-center fw-semibold"
                        dateFormat="dd.MM.yyyy"
                        locale="ru"
                      />
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="text-muted small fw-semibold me-2">Смена:</span>
                      <CFormSelect
                        size="sm"
                        value={statsModal.selectedShiftId}
                        onChange={(e) => handleShiftFilterApply(e.target.value, statsModal.baseDate)}
                        style={{ minWidth: "150px", cursor: "pointer" }}
                        className="fw-semibold text-primary"
                      >
                        <option value="">-- Выберите --</option>
                        {shiftOptions.map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.start.substring(0, 5)} - {s.end.substring(0, 5)})</option>
                        ))}
                      </CFormSelect>
                    </div>
                  </div>
                )}
              </div>

              {/* УЧАСТОК И EXPORT (Внизу) */}
              <div className="d-flex flex-wrap justify-content-between align-items-center w-100 mt-2 border-top pt-3">
                {!statsModal.targetTruck ? (
                  <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: "200px", maxWidth: "350px" }}>
                    <CIcon icon={cilMap} className="text-muted me-2" />
                    <CFormSelect
                      size="sm"
                      value={statsModal.workZone || ""}
                      onChange={(e) => {
                        const newZone = e.target.value;
                        const targetTz = updateModalDeviceContext(statsModal.targetTruck, newZone);
                        setStatsModal((prev) => ({ ...prev, workZone: newZone }));
                        loadTruckStats(statsModal.targetTruck, statsModal.startDate, statsModal.endDate, newZone, selectedOrgFilter, targetTz);
                      }}
                      className="fw-semibold text-primary"
                    >
                      <option value="">Все участки</option>
                      {filteredWorkZones.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.name}
                        </option>
                      ))}
                    </CFormSelect>
                  </div>
                ) : <div />}

                <CButton
                  size="sm"
                  color="success"
                  variant="outline"
                  onClick={handleExportExcel}
                  disabled={exportingExcel || !statsModal.data}
                  className="fw-bold"
                >
                  {exportingExcel ? (
                    <CSpinner size="sm" />
                  ) : (
                    <><CIcon icon={cilCloudDownload} className="me-1" /> Excel</>
                  )}
                </CButton>
              </div>

            </div>
          </div>

          {statsModal.isDataIncomplete && !statsModal.loading && (
            <div className="alert alert-warning d-flex align-items-center mb-4 shadow-sm border-warning border-opacity-50">
              <CIcon icon={cilWarning} size="xl" className="me-3 text-warning flex-shrink-0" />
              <div>
                <h6 className="fw-bold mb-1 text-dark">Возможно, данные за этот период еще не выгрузились</h6>
                <span className="small text-dark">
                  Текущий объем выработки (<strong>{statsModal.data?.global_summary?.volume} м³</strong>) значительно
                  ниже расчетной нормы для этого периода (ожидалось около{" "}
                  <strong>{statsModal.expectedVolume?.toFixed(1)} м³</strong>).
                </span>
              </div>
            </div>
          )}

          {statsModal.loading ? (
            <div className="text-center p-5">
              <CSpinner color="primary" />
            </div>
          ) : (
            <>
              <CRow className="mb-4 g-3">
                <CCol md={3} xs={6}>
                  <CCard className="h-100 border-0 border-start border-start-4 border-start-primary shadow-sm">
                    <CCardBody className="text-center">
                      <div className="text-muted text-uppercase fw-bold small mb-1">Заездов (раз)</div>
                      <div className="fs-3 fw-bold text-dark lh-1">
                        {statsModal.data?.global_summary?.arrivals || 0}
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
                <CCol md={3} xs={6}>
                  <CCard className="h-100 border-0 border-start border-start-4 border-start-info shadow-sm">
                    <CCardBody className="text-center">
                      <div className="text-muted text-uppercase fw-bold small mb-1">Ср. Ковшей</div>
                      <div className="fs-3 fw-bold text-info lh-1">
                        {statsModal.data?.global_summary?.avg_cycles || 0}
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
                <CCol md={3} xs={6}>
                  <CCard className="h-100 border-0 border-start border-start-4 border-start-warning shadow-sm">
                    <CCardBody className="text-center">
                      <div className="text-muted text-uppercase fw-bold small mb-1">Ср. Время загрузки</div>
                      <div className="fs-3 fw-bold text-warning lh-1">
                        {formatDuration(statsModal.data?.global_summary?.avg_time_sec)}
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
                <CCol md={3} xs={6}>
                  <CCard className="h-100 border-0 border-start border-start-4 border-start-success shadow-sm">
                    <CCardBody className="text-center">
                      <div className="text-muted text-uppercase fw-bold small mb-1">Объем грунта</div>
                      <div className="fs-3 fw-bold text-success lh-1">
                        {statsModal.data?.global_summary?.volume || 0} <span className="fs-6">м³</span>
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              {!statsModal.targetTruck && statsModal.data?.trucks_summary?.length > 0 && (
                <CCard className="shadow-sm border-0 mb-4">
                  <CCardHeader className="bg-white fw-bold py-3 text-primary">Группировка по машинам</CCardHeader>
                  <div className="table-responsive" style={{ maxHeight: "300px" }}>
                    <CTable hover align="middle" className="mb-0 border-top-0" small>
                      <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                        <CTableRow>
                          <CTableHeaderCell className="ps-3">Самосвал</CTableHeaderCell>
                          <CTableHeaderCell className="text-center">Заездов</CTableHeaderCell>
                          <CTableHeaderCell className="text-center">Ср. Ковшей</CTableHeaderCell>
                          <CTableHeaderCell className="text-center">Ср. Время</CTableHeaderCell>
                          <CTableHeaderCell className="text-end pe-3">Суммарный Объем</CTableHeaderCell>
                        </CTableRow>
                      </CTableHead>
                      <CTableBody>
                        {statsModal.data.trucks_summary.map((t) => (
                          <CTableRow key={t.id}>
                            <CTableDataCell className="fw-semibold ps-3">
                              <div className="d-flex align-items-center">
                                <div
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: t.color || "#ccc",
                                    borderRadius: "50%",
                                    marginRight: "8px",
                                  }}
                                ></div>
                                <span className={t.id === "unidentified" ? "fst-italic text-muted" : ""}>{t.name}</span>
                              </div>
                            </CTableDataCell>
                            <CTableDataCell
                              className={`text-center fw-bold ${t.id === "unidentified" ? "text-muted" : ""}`}
                            >
                              {t.arrivals}
                            </CTableDataCell>
                            <CTableDataCell className={`text-center ${t.id === "unidentified" ? "text-muted" : ""}`}>
                              {t.avg_cycles}
                            </CTableDataCell>
                            <CTableDataCell className={`text-center ${t.id === "unidentified" ? "text-muted" : ""}`}>
                              {formatDuration(t.avg_time_sec)}
                            </CTableDataCell>
                            <CTableDataCell
                              className={`text-end pe-3 fw-bold ${t.id === "unidentified" ? "text-muted" : "text-success"}`}
                            >
                              {Number(t.volume).toFixed(2)} м³
                            </CTableDataCell>
                          </CTableRow>
                        ))}
                      </CTableBody>
                    </CTable>
                  </div>
                </CCard>
              )}

              <CCard className="shadow-sm border-0">
                <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center py-3">
                  <span>
                    <CIcon icon={cilList} className="me-2 text-info" />
                    {i18n.t('truck_fleet_page.details_of_each_trip', 'Детализация каждого заезда')}
                  </span>
                  <span className="badge bg-secondary rounded-pill">
                    {displayedDetails.length} записей
                  </span>
                </CCardHeader>
                <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                  <CTable hover align="middle" className="mb-0 border-top-0">
                    <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <CTableRow>
                        <CTableHeaderCell className="ps-3">Дата / Время</CTableHeaderCell>
                        <CTableHeaderCell>Экскаватор</CTableHeaderCell>
                        {!statsModal.targetTruck && <CTableHeaderCell>Самосвал</CTableHeaderCell>}
                        <CTableHeaderCell className="text-center">Время загрузки</CTableHeaderCell>
                        <CTableHeaderCell className="text-center">Ковшей</CTableHeaderCell>
                        <CTableHeaderCell className="text-end pe-3">Объем</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {displayedDetails.length > 0 ? (
                        displayedDetails
                          .slice((detailPage - 1) * itemsPerPage, detailPage * itemsPerPage)
                          .map((row) => {
                            const fakeStart = getFakeLocalDate(row.start_time, row.timezone_offset);
                            const fakeEnd = getFakeLocalDate(row.end_time, row.timezone_offset);
                            return (
                              <CTableRow key={row.id}>
                                <CTableDataCell className="text-muted ps-3">
                                  <div className="fw-semibold text-dark">{dayjs(fakeStart).format("DD.MM.YYYY")}</div>
                                  <div className="small">
                                    {dayjs(fakeStart).format("HH:mm:ss")} - {dayjs(fakeEnd).format("HH:mm:ss")}
                                  </div>
                                </CTableDataCell>
                                <CTableDataCell className="fw-semibold">{row.device_name}</CTableDataCell>
                                {!statsModal.targetTruck && (
                                  <CTableDataCell>
                                    <span
                                      className={`badge bg-light border ${row.truck_id === "unidentified" ? "text-muted fst-italic" : "text-dark"}`}
                                    >
                                      <div
                                        style={{
                                          display: "inline-block",
                                          width: "8px",
                                          height: "8px",
                                          backgroundColor: row.truck_color || "#ccc",
                                          borderRadius: "50%",
                                          marginRight: "6px",
                                        }}
                                      ></div>
                                      {row.truck_name}
                                    </span>
                                  </CTableDataCell>
                                )}
                                <CTableDataCell className="text-center fw-bold">
                                  {formatDuration(row.duration_sec)}
                                </CTableDataCell>
                                <CTableDataCell className="text-center">
                                  <span
                                    className={`badge fs-6 ${row.truck_id === "unidentified" ? "bg-secondary" : "bg-primary"}`}
                                  >
                                    {row.cycles}
                                  </span>
                                </CTableDataCell>
                                <CTableDataCell
                                  className={`text-end pe-3 fw-bold fs-6 ${row.truck_id === "unidentified" ? "text-muted" : "text-success"}`}
                                >
                                  {Number(row.volume).toFixed(2)} м³
                                </CTableDataCell>
                              </CTableRow>
                            );
                          })
                      ) : (
                        <CTableRow>
                          <CTableDataCell colSpan={6} className="text-center text-muted p-5">
                            {i18n.t('truck_fleet_page.no_data', 'Нет данных')}
                          </CTableDataCell>
                        </CTableRow>
                      )}
                    </CTableBody>
                  </CTable>
                </div>
                {displayedDetails.length > itemsPerPage && (
                  <div className="p-2 bg-white border-top d-flex justify-content-center">
                    <CPagination size="sm">
                      <CPaginationItem
                        disabled={detailPage === 1}
                        onClick={() => setDetailPage(detailPage - 1)}
                      >
                        Пред.
                      </CPaginationItem>
                      <CPaginationItem active>
                        {detailPage} / {Math.ceil(displayedDetails.length / itemsPerPage)}
                      </CPaginationItem>
                      <CPaginationItem
                        disabled={detailPage >= Math.ceil(displayedDetails.length / itemsPerPage)}
                        onClick={() => setDetailPage(detailPage + 1)}
                      >
                        След.
                      </CPaginationItem>
                    </CPagination>
                  </div>
                )}
              </CCard>
            </>
          )}
        </CModalBody>
        <CModalFooter className="bg-light border-top-0 pt-0">
          <CButton
            color="secondary"
            className="px-4"
            onClick={() => setStatsModal({ ...statsModal, visible: false })}
          >
            Закрыть
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default TruckFleetPage;